import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const stats = [
  { testid: "stat-roi-card", value: 340, prefix: "+", suffix: "%", label: "Average client ROI" },
  { testid: "stat-uptime-card", value: 99.8, prefix: "", suffix: "%", label: "System uptime delivered", decimals: 1 },
  { testid: "stat-value-card", value: 45, prefix: "$", suffix: "M", label: "Client value created" },
  { testid: "stat-timeline-card", value: 14, prefix: "", suffix: " days", label: "From brief to prototype" },
];

const Counter = ({ value, prefix, suffix, decimals = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1600;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 4);
      setDisplay(value * eased);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      {display.toFixed(decimals)}
      {suffix}
    </span>
  );
};

export const Stats = () => (
  <section
    id="results"
    data-testid="results-section"
    className="relative py-20 lg:py-28 bg-[#0B0F19] grain overflow-hidden"
  >
    <div
      className="absolute top-0 left-1/3 w-[520px] h-[520px] rounded-full opacity-[0.14] pointer-events-none"
      style={{ background: "radial-gradient(circle, #0FA89E 0%, transparent 65%)" }}
    />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14 lg:mb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-teal-400 font-semibold mb-4">
            Results
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white max-w-lg">
            The return on building for you, not around them
          </h2>
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="text-slate-400 text-base max-w-sm leading-relaxed"
        >
          Off-the-shelf software charges you rent forever and fits like a
          rental. Custom tools pay for themselves. Here's the proof.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((s, i) => (
          <motion.div
            key={s.testid}
            data-testid={s.testid}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-8"
          >
            <div className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight mb-3">
              <Counter value={s.value} prefix={s.prefix} suffix={s.suffix} decimals={s.decimals} />
            </div>
            <p className="text-sm text-slate-400 font-medium">{s.label}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
